import React from 'react';
import { Link } from 'react-router-dom';

function CareerInfo() {
  const coreValues = [
    {
      icon: 'fa-lightbulb',
      title: 'Innovation First',
      description: 'We encourage creative thinking and embrace new ideas. Our team members are empowered to challenge the status quo and drive meaningful change.',
      highlights: ['Innovation Labs', 'Hackathons', 'R&D Time']
    },
    {
      icon: 'fa-users',
      title: 'Collaborative Culture',
      description: 'Teamwork is at our core. We believe the best solutions come from diverse perspectives working together towards common goals.',
      highlights: ['Cross-functional Teams', 'Knowledge Sharing', 'Mentorship']
    },
    {
      icon: 'fa-trophy',
      title: 'Excellence & Quality',
      description: 'We maintain the highest standards in everything we do, delivering exceptional value to our clients and continuous improvement.',
      highlights: ['Quality Standards', 'Best Practices', 'Continuous Learning']
    },
    {
      icon: 'fa-heart',
      title: 'People-Centric',
      description: 'Our employees are our greatest asset. We invest in their growth, well-being, and success both professionally and personally.',
      highlights: ['Work-Life Balance', 'Growth Opportunities', 'Wellness Programs']
    }
  ];

  const careerPaths = [
    {
      icon: 'fa-code',
      title: 'Technical Track',
      description: 'Build deep expertise in cutting-edge technologies',
      roles: ['Software Engineer', 'Senior Engineer', 'Principal Engineer', 'Distinguished Engineer', 'Technical Fellow'],
      color: '#0D6EFD'
    },
    {
      icon: 'fa-users-gear',
      title: 'Leadership Track',
      description: 'Lead teams and drive organizational success',
      roles: ['Team Lead', 'Engineering Manager', 'Director', 'VP of Engineering', 'CTO'],
      color: '#6C63FF'
    },
    {
      icon: 'fa-lightbulb',
      title: 'Consulting Track',
      description: 'Guide clients through complex transformations',
      roles: ['Consultant', 'Senior Consultant', 'Principal Consultant', 'Partner', 'Managing Partner'],
      color: '#2ECC71'
    },
    {
      icon: 'fa-chart-line',
      title: 'Specialist Track',
      description: 'Become a recognized expert in your domain',
      roles: ['Specialist', 'Senior Specialist', 'Subject Matter Expert', 'Principal Specialist', 'Chief Specialist'],
      color: '#E74C3C'
    }
  ];

  const workEnvironment = [
    {
      icon: 'fa-building',
      title: 'Modern Offices',
      description: 'State-of-the-art workspaces designed for collaboration and productivity',
      features: ['Standing Desks', 'Breakout Spaces', 'Quiet Rooms', 'Game Rooms', 'Cafeterias']
    },
    {
      icon: 'fa-laptop-house',
      title: 'Remote Flexibility',
      description: 'Work from anywhere with our flexible remote-first approach',
      features: ['Home Office Stipend', 'Flexible Hours', 'Global Teams', 'Digital Tools', 'Virtual Events']
    },
    {
      icon: 'fa-globe',
      title: 'Global Presence',
      description: 'Offices and teams across 25+ countries worldwide',
      features: ['International Projects', 'Cultural Diversity', 'Global Mobility', 'Language Training', 'Travel Opportunities']
    },
    {
      icon: 'fa-rocket',
      title: 'Innovation Hubs',
      description: 'Dedicated spaces for experimentation and breakthrough thinking',
      features: ['Prototyping Labs', 'Innovation Time', 'Tech Showcase', 'Client Demos', 'Startup Collaboration']
    }
  ];

  const learningOpportunities = [
    {
      category: 'Technical Skills',
      icon: 'fa-laptop-code',
      programs: [
        'Cloud Certifications (AWS, Azure, GCP)',
        'Programming Languages Bootcamps',
        'DevOps & CI/CD Training',
        'Cybersecurity Workshops',
        'Data Science & AI Courses',
        'Mobile Development Training'
      ]
    },
    {
      category: 'Business Skills',
      icon: 'fa-briefcase',
      programs: [
        'Project Management (PMP, Agile)',
        'Business Analysis Certification',
        'Financial Acumen Training',
        'Strategic Planning Workshops',
        'Client Relationship Management',
        'Consulting Methodologies'
      ]
    },
    {
      category: 'Leadership & Soft Skills',
      icon: 'fa-people-group',
      programs: [
        'Executive Leadership Program',
        'Communication Mastery',
        'Emotional Intelligence Training',
        'Conflict Resolution Workshops',
        'Presentation Skills Coaching',
        'Negotiation Techniques'
      ]
    },
    {
      category: 'Industry Expertise',
      icon: 'fa-industry',
      programs: [
        'SAP Solutions Deep Dive',
        'ERP System Implementations',
        'Industry-Specific Training',
        'Digital Transformation Strategies',
        'Emerging Technologies Seminar',
        'Client Success Programs'
      ]
    }
  ];

  const diversityStats = [
    { percentage: '45%', label: 'Women in Leadership' },
    { percentage: '60+', label: 'Nationalities' },
    { percentage: '12', label: 'Employee Resource Groups' },
    { percentage: '100%', label: 'Equal Pay Certified' }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      role: 'Senior Cloud Architect',
      years: '5 years',
      image: 'https://randomuser.me/api/portraits/women/44.jpg',
      quote: 'Time2Find gave me the platform to grow from a junior developer to leading cloud transformations for Fortune 500 clients. The learning opportunities are endless.',
      highlights: ['Career Growth', 'Mentorship', 'Global Projects']
    },
    {
      name: 'Michael Chen',
      role: 'SAP Consultant',
      years: '7 years',
      image: 'https://randomuser.me/api/portraits/men/32.jpg',
      quote: 'The collaborative culture here is unmatched. Working with brilliant minds from around the world has expanded my perspective and skillset tremendously.',
      highlights: ['Team Collaboration', 'Innovation', 'Work-Life Balance']
    },
    {
      name: 'Priya Patel',
      role: 'Data Science Lead',
      years: '4 years',
      image: 'https://randomuser.me/api/portraits/women/68.jpg',
      quote: 'I appreciate the company\'s commitment to diversity and inclusion. As a woman in tech, I\'ve always felt supported and empowered to reach my full potential.',
      highlights: ['Diversity', 'Leadership Support', 'Impact']
    }
  ];

  const hiringProcess = [
    {
      step: '01',
      title: 'Application Review',
      duration: '1-2 days',
      description: 'Our recruitment team carefully reviews your application and resume to assess your qualifications and experience.',
      icon: 'fa-file-lines'
    },
    {
      step: '02',
      title: 'Phone Screening',
      duration: '30 minutes',
      description: 'Initial conversation with our recruiter to discuss your background, career goals, and interest in the role.',
      icon: 'fa-phone'
    },
    {
      step: '03',
      title: 'Technical Assessment',
      duration: '1-2 hours',
      description: 'Demonstrate your technical skills through coding challenges, case studies, or practical assignments relevant to the position.',
      icon: 'fa-laptop-code'
    },
    {
      step: '04',
      title: 'Team Interviews',
      duration: '2-3 hours',
      description: 'Meet with potential team members and managers through multiple interview rounds to assess culture fit and expertise.',
      icon: 'fa-users'
    },
    {
      step: '05',
      title: 'Final Interview',
      duration: '1 hour',
      description: 'Discussion with senior leadership about your vision, expectations, and how you can contribute to our mission.',
      icon: 'fa-handshake'
    },
    {
      step: '06',
      title: 'Offer & Onboarding',
      duration: '1-2 weeks',
      description: 'Receive your offer, negotiate terms if needed, and begin your journey with a comprehensive onboarding program.',
      icon: 'fa-rocket'
    }
  ];

  return (
    <div className="career-info-page">
      {/* Hero Section */}
      <section className="career-info-hero">
        <div className="career-info-hero-overlay"></div>
        <div className="career-info-hero-content">
          <h1 className="career-info-hero-title">
            Your Career <span className="highlight-blue">Journey</span> Starts Here
          </h1>
          <p className="career-info-hero-subtitle">
            Discover what makes Time2Find an exceptional place to build your career. 
            Learn about our culture, growth opportunities, and the impact you can make.
          </p>
          <div className="career-info-hero-actions">
            <Link to="/career" className="career-info-btn-primary">
              View Open Positions
            </Link>
            <button 
              className="career-info-btn-secondary"
              onClick={() => document.getElementById('culture-section')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Explore Culture
            </button>
          </div>
        </div>
      </section>

      {/* Why Join Us Section */}
      <section className="career-info-why-join">
        <div className="career-info-container">
          <div className="career-info-section-header">
            <h2 className="career-info-section-title">Why Join Time2Find?</h2>
            <p className="career-info-section-subtitle">
              We're more than just a workplace – we're a community of innovators, problem-solvers, 
              and passionate professionals dedicated to making a difference.
            </p>
          </div>

          <div className="why-join-content">
            <div className="why-join-text">
              <h3 className="why-join-heading">Shape the Future of Technology</h3>
              <p className="why-join-paragraph">
                At Time2Find, you'll work on transformative projects that impact businesses and 
                communities worldwide. From implementing cutting-edge cloud solutions to driving 
                digital transformation for Fortune 500 companies, your work will have real meaning and measurable impact.
              </p>
              <p className="why-join-paragraph">
                We believe in empowering our employees with the autonomy to innovate, the resources 
                to excel, and the support to grow. Whether you're just starting your career or are 
                an experienced professional, you'll find opportunities to challenge yourself and 
                reach new heights.
              </p>
              <div className="why-join-highlights">
                <div className="highlight-item">
                  <i className="fas fa-check-circle"></i>
                  <span>Work on global projects with industry leaders</span>
                </div>
                <div className="highlight-item">
                  <i className="fas fa-check-circle"></i>
                  <span>Access to latest technologies and tools</span>
                </div>
                <div className="highlight-item">
                  <i className="fas fa-check-circle"></i>
                  <span>Competitive compensation and benefits</span>
                </div>
                <div className="highlight-item">
                  <i className="fas fa-check-circle"></i>
                  <span>Continuous learning and development opportunities</span>
                </div>
              </div>
            </div>
            <div className="why-join-image">
              <img 
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=800&h=600&fit=crop" 
                alt="Team collaboration" 
              />
            </div>
          </div>
        </div>
      </section>

      {/* Core Values Section */}
      <section className="career-info-values" id="culture-section">
        <div className="career-info-container">
          <div className="career-info-section-header">
            <h2 className="career-info-section-title">Our Core Values</h2>
            <p className="career-info-section-subtitle">
              These principles guide our decisions, shape our culture, and define who we are.
            </p>
          </div>

          <div className="values-grid-detailed">
            {coreValues.map((value, index) => (
              <div key={index} className="value-card-detailed">
                <div className="value-icon-large">
                  <i className={`fas ${value.icon}`}></i>
                </div>
                <h3 className="value-card-title">{value.title}</h3>
                <p className="value-card-description">{value.description}</p>
                <div className="value-highlights">
                  {value.highlights.map((highlight, idx) => (
                    <span key={idx} className="value-highlight-tag">{highlight}</span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Career Paths Section */}
      <section className="career-info-paths">
        <div className="career-info-container">
          <div className="career-info-section-header">
            <h2 className="career-info-section-title">Career Growth Paths</h2>
            <p className="career-info-section-subtitle">
              Multiple pathways to advance your career based on your interests and strengths.
            </p>
          </div>

          <div className="career-paths-grid">
            {careerPaths.map((path, index) => (
              <div key={index} className="career-path-card" style={{ borderTopColor: path.color }}>
                <div className="career-path-icon" style={{ backgroundColor: `${path.color}15`, color: path.color }}>
                  <i className={`fas ${path.icon}`}></i>
                </div>
                <h3 className="career-path-title">{path.title}</h3>
                <p className="career-path-description">{path.description}</p>
                <div className="career-path-progression">
                  {path.roles.map((role, idx) => (
                    <div key={idx} className="progression-step">
                      <div className="progression-dot" style={{ backgroundColor: path.color }}></div>
                      <span className="progression-role">{role}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Work Environment Section */}
      <section className="career-info-environment">
        <div className="career-info-container">
          <div className="career-info-section-header">
            <h2 className="career-info-section-title">Work Environment</h2>
            <p className="career-info-section-subtitle">
              Flexible, collaborative spaces designed for your success and well-being.
            </p>
          </div>

          <div className="environment-grid">
            {workEnvironment.map((env, index) => (
              <div key={index} className="environment-card">
                <div className="environment-icon">
                  <i className={`fas ${env.icon}`}></i>
                </div>
                <h3 className="environment-title">{env.title}</h3>
                <p className="environment-description">{env.description}</p>
                <ul className="environment-features">
                  {env.features.map((feature, idx) => (
                    <li key={idx}>
                      <i className="fas fa-check"></i>
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Learning & Development Section */}
      <section className="career-info-learning">
        <div className="career-info-container">
          <div className="career-info-section-header">
            <h2 className="career-info-section-title">Learning & Development</h2>
            <p className="career-info-section-subtitle">
              Invest in yourself with our comprehensive training programs and certifications.
            </p>
          </div>

          <div className="learning-grid">
            {learningOpportunities.map((opportunity, index) => (
              <div key={index} className="learning-card">
                <div className="learning-header">
                  <div className="learning-icon">
                    <i className={`fas ${opportunity.icon}`}></i>
                  </div>
                  <h3 className="learning-category">{opportunity.category}</h3>
                </div>
                <ul className="learning-programs">
                  {opportunity.programs.map((program, idx) => (
                    <li key={idx}>
                      <i className="fas fa-graduation-cap"></i>
                      {program}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Diversity & Inclusion Section */}
      <section className="career-info-diversity">
        <div className="career-info-container">
          <div className="diversity-content">
            <div className="diversity-text">
              <h2 className="diversity-title">Diversity & Inclusion</h2>
              <p className="diversity-description">
                We're committed to building a diverse workforce and fostering an inclusive environment 
                where everyone feels valued, respected, and empowered to contribute their unique perspectives.
              </p>
              <p className="diversity-description">
                Our Employee Resource Groups (ERGs) provide support, networking, and advocacy for 
                underrepresented communities. We celebrate differences and believe that diverse teams 
                drive better innovation and results.
              </p>
              <div className="diversity-stats-grid">
                {diversityStats.map((stat, index) => (
                  <div key={index} className="diversity-stat">
                    <div className="diversity-stat-number">{stat.percentage}</div>
                    <div className="diversity-stat-label">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="diversity-image">
              <img 
                src="https://images.unsplash.com/photo-1529156069898-49953e39b3ac?w=800&h=600&fit=crop" 
                alt="Diverse team" 
              />
            </div>
          </div>
        </div>
      </section>

      {/* Employee Testimonials Section */}
      <section className="career-info-testimonials">
        <div className="career-info-container">
          <div className="career-info-section-header">
            <h2 className="career-info-section-title">Hear from Our Team</h2>
            <p className="career-info-section-subtitle">
              Real stories from real employees about their experience at Time2Find.
            </p>
          </div>

          <div className="testimonials-grid-detailed">
            {testimonials.map((testimonial, index) => (
              <div key={index} className="testimonial-card-detailed">
                <div className="testimonial-header">
                  <img src={testimonial.image} alt={testimonial.name} className="testimonial-avatar" />
                  <div className="testimonial-info">
                    <h4 className="testimonial-name">{testimonial.name}</h4>
                    <p className="testimonial-role">{testimonial.role}</p>
                    <p className="testimonial-years">
                      <i className="fas fa-clock"></i> {testimonial.years}
                    </p>
                  </div>
                </div>
                <p className="testimonial-quote">"{testimonial.quote}"</p>
                <div className="testimonial-highlights">
                  {testimonial.highlights.map((highlight, idx) => (
                    <span key={idx} className="testimonial-tag">
                      <i className="fas fa-star"></i> {highlight}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Hiring Process Section */}
      <section className="career-info-hiring-process">
        <div className="career-info-container">
          <div className="career-info-section-header">
            <h2 className="career-info-section-title">Our Hiring Process</h2>
            <p className="career-info-section-subtitle">
              Transparent and efficient – here's what to expect when you apply.
            </p>
          </div>

          <div className="hiring-process-timeline">
            {hiringProcess.map((step, index) => (
              <div key={index} className="process-step">
                <div className="process-step-number">{step.step}</div>
                <div className="process-step-content">
                  <div className="process-step-header">
                    <div className="process-step-icon">
                      <i className={`fas ${step.icon}`}></i>
                    </div>
                    <div className="process-step-info">
                      <h3 className="process-step-title">{step.title}</h3>
                      <span className="process-step-duration">
                        <i className="fas fa-hourglass-half"></i> {step.duration}
                      </span>
                    </div>
                  </div>
                  <p className="process-step-description">{step.description}</p>
                </div>
                {index < hiringProcess.length - 1 && <div className="process-connector"></div>}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="career-info-cta">
        <div className="career-info-cta-content">
          <h2 className="career-info-cta-title">Ready to Start Your Journey?</h2>
          <p className="career-info-cta-text">
            Join our talented team and help shape the future of technology. 
            Explore our open positions and find the perfect role for you.
          </p>
          <div className="career-info-cta-actions">
            <Link to="/career" className="career-info-cta-btn-primary">
              <i className="fas fa-briefcase"></i>
              View Open Positions
            </Link>
            <Link to="/contact" className="career-info-cta-btn-secondary">
              <i className="fas fa-envelope"></i>
              Contact Recruitment Team
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default CareerInfo;
